import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { fromC, toC } from 'altrac-conversions'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'

// HOC
import withAppStore from '../../../util/withAppStore'

// form components
import Bindicator from './Bindicator'
import { ButtonList, isNumber } from '../Inputs/Inputs'
import EngineRunning from './EngineRunning'
import General from './General'
import InputContactInfo from '../Inputs/InputContactInfo'
import PumpPressure from './PumpPressure'
import PumpState from './PumpState'
import Simple from './Simple'

// icons
import Battery from '../Graphics/Battery'
import Pressure from '../Graphics/Pressure'
import Pump from '../Graphics/Pump'
import Fuel from '../Graphics/Fuel'
import { SpeedometerOff, SpeedometerOn } from '../Graphics/Speedometer'
import Thermometer from '../Graphics/Thermometer'
import Moisture from '../Graphics/Moisture'
import High from '../Graphics/High'
import Low from '../Graphics/Low'

// styles
import styles from '../../../../css/settings.module.css'

const RuleDetail = ({
  rule,
  saveButtonCallback,
  device,
  address,
  level,
  tempConv,
  intl: { formatMessage },
}) => {
  const [template, setTemplate] = useState(rule ? rule?.rule?.template : '')
  const [disabledSelect, setDisabledSelect] = useState((rule && rule.rule))

  const updateNewRule = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'update-new-rule-basic',
      address,
      active: 'true',
      nextNotification: '0',
    })
  }, [address])

  const childChange = useCallback((event) => {
    if (event.target.name === 'template') {
      setTemplate(event.target.value)
    }

    setDisabledSelect(false)
    saveButtonCallback()
  }, [saveButtonCallback])

  useEffect(() => {
    updateNewRule()
    if (template) saveButtonCallback()
  }, [updateNewRule, template, saveButtonCallback])

  let ruleTemplate

  const {
    configuration: {
      model,
    } = {},
    interface_versioned: interfaceVersioned,
    physical: {
      deviceNumber,
      deviceType,
      powered,
    } = {},
  } = device || {}

  const {
    rule: {
      multiplier,
      test,
      testValue,
      valueKey,
    } = {},
    transport: {
      addresses,
      body,
      debounceTime = 0,
      method = '',
      notificationInterval = 900000,
      sendOnce = true,
      subject = '',
      type = 'trigger',
    } = {},
  } = rule || {}

  let {
    rule: {
      formula,
    } = {},
  } = rule || {}

  const newRule = AppStore.getNewRule()

  const {
    data: {
      primaryPage: {
        telemetryTypes = [],
      } = {},
      report,
      report: {
        flowRate,
        pressureAfter,
        pressureBefore,
        pressureLegacy,
        relay1Run,
        relay1Signal,
        relay2Run,
        relay2Signal,
      } = {},
    } = {},
  } = interfaceVersioned || {}

  const AMBIENT_TEMPERATURE = (
    formatMessage({
      id: 'sensor.temperature.ambient.long',
      defaultMessage: 'Ambient Temperature',
      description: 'Ambient Temperature label',
    })
  )

  const AMBIENT_TEMPERATURE_MESSAGE = `Temperature at device {{deviceNumber}} is {{128,${tempConv === 'f' ? 'temperatureF' : 'temperatureC'},1}} ${tempConv === 'f' ? 'fahrenheit' : 'celsius'}`

  const AVERAGE_SOIL_MOISTURE_LOW_MESSAGE = 'Soil moisture at device {{deviceNumber}} is low. Current average value is {{formula}}'

  const AVERAGE_SOIL_MOISTURE_HIGH_MESSAGE = 'Soil moisture at device {{deviceNumber}} is high. Current average value is {{formula}}'

  const AVERAGE_SOIL_SALINITY_LOW_MESSAGE = 'Soil salinity at device {{deviceNumber}} is low. Current average value is {{formula}}'

  const AVERAGE_SOIL_SALINITY_HIGH_MESSAGE = 'Soil salinity at device {{deviceNumber}} is high. Current average value is {{formula}}'

  const AVERAGE_SOIL_TEMPERATURE_HIGH_MESSAGE = 'Soil temperature at device {{deviceNumber}} is high. Current average value is {{formula}}'

  const AVERAGE_SOIL_TEMPERATURE_LOW_MESSAGE = 'Soil temperature at device {{deviceNumber}} is low. Current average value is {{formula}}'

  const BATTERY_VOLTAGE_MESSAGE = 'Battery Voltage at device {{deviceNumber}} is {{130,percentTo20V,1}} volts'

  const BATTERY_VOLTAGE_LOW_MESSAGE = 'Battery Voltage at device {{deviceNumber}} is low. Current voltage is {{130,percentTo20V,1}}'

  const BATTERY_VOLTAGE_DISCONNECTED_MESSAGE = 'Battery at device {{deviceNumber}} is disconnected. Current voltage is {{130,percentTo20V,1}}'

  const INTERNAL_BATTERY_MESSAGE = 'Internal Battery at device {{deviceNumber}} is {{129,default,0.01}}%'

  const POWER_DISCONNECTED_MESSAGE = 'Power at device {{deviceNumber}} is disconnected'

  const FUEL_LEVEL_MESSAGE = 'Fuel level at device {{deviceNumber}} is {{133,fuelLevel,0.01}}%'

  const WIND_MACHINE_RUNNING_MESSAGE = `Wind Machine {{deviceNumber}} is ${testValue ? 'stopping' : 'starting'}`

  const WIND_MACHINE_STARTUP_MESSAGE = `Wind Machine {{deviceNumber}} is starting, temperature is {{128,${tempConv === 'f' ? 'temperatureF' : 'temperatureC'},1}} ${tempConv === 'f' ? 'fahrenheit' : 'celsius'}`

  const WIND_MACHINE_STOP_MESSAGE = 'Wind Machine {{deviceNumber}} is stopping'

  const WIND_MACHINE_FAILED_TO_START_MESSAGE = 'Wind Machine {{deviceNumber}} failed to start'

  const SOIL_MOISTURE = (
    formatMessage({
      id: 'sensor.soil.moisture',
      defaultMessage: 'Soil Moisture',
      description: 'soil moisture label',
    })
  )

  const SOIL_SALINITY = (
    formatMessage({
      id: 'sensor.soil.salinity',
      defaultMessage: 'Soil Salinity',
      description: 'soil salinity label',
    })
  )

  const SOIL_TEMPERATURE = (
    formatMessage({
      id: 'sensor.soil.temperature',
      defaultMessage: 'Soil Temperature',
      description: 'soil temperature label',
    })
  )

  const PRESSURE_MESSAGE = 'Pressure at device {{deviceNumber}} is {{133,fourToTwenty,1}}'

  const AMBIENT_TEMPERATURE_VALUEKEY = 128
  const BATTERY_VOLTAGE_VALUEKEY = 130
  const FUEL_LEVEL_VALUEKEY = 133
  const ENGINE_RUNNING_VALUEKEY = 131
  const ENGINE_FAILED_TO_START_VALUEKEY = 140
  const INTERNAL_BATTERY_VOLTAGE_VALUEKEY = 129

  const valueKeysFor = (typeName) => {
    const accumulator = []
    telemetryTypes.forEach((obj) => ((obj.type === typeName) && accumulator.push(obj.valueKey)))
    return accumulator
  }

  const soilMoistureValueKeys = valueKeysFor('soilMoisture')
  const soilSalinityValueKeys = valueKeysFor('soilSalinity')
  const soilTemperatureValueKeys = valueKeysFor('soilTemperature')

  const formatNotificationMessage = (message, key, multiplierFormat = 1) =>
    message.replace('formula', `[${key.toString()}],${formula},${multiplierFormat}`)

  const testValueOr = (x) => (isNumber(testValue) ? testValue : x)

  let run
  let signal

  if (/^pumpRunning|pumpShouldBeRunning|pumpStopped$/.test(template)) {
    const {
      rule: {
        valueKey: {
          run: {
            valueKey: runValueKey,
          } = {},
        } = {},
      } = {},
    } = rule || newRule || {}

    if (runValueKey === 'R1') {
      run = relay1Run
      signal = relay1Signal
    } else if (runValueKey === 'R2') {
      run = relay2Run
      signal = relay2Signal
    }
  }

  const isPumpV2 = () => model === 'pump2'
  const isPumpV1 = () => !(isPumpV2())

  switch (template) {
    case 'temperatureLessThan':
      ruleTemplate = (
        <Simple
          template={template}
          test='lessThan'
          sensor={`${AMBIENT_TEMPERATURE} (${tempConv === 'f' ? 'F' : 'C'})`}
          formula='temperatureC'
          valueKey={AMBIENT_TEMPERATURE_VALUEKEY}
          testValue={testValueOr((tempConv === 'f' ? 0.5556 : 1))}
          testValueDisplay={fromC}
          testValueDisplayReverse={toC}
          locale={tempConv}
          multiplier='1'
          body={body || AMBIENT_TEMPERATURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'temperatureGreaterThan':
      ruleTemplate = (
        <Simple
          template={template}
          test='greaterThan'
          sensor={`${AMBIENT_TEMPERATURE} (${tempConv === 'f' ? 'F' : 'C'})`}
          formula='temperatureC'
          valueKey={AMBIENT_TEMPERATURE_VALUEKEY}
          testValue={testValueOr((tempConv === 'f' ? 26.6667 : 25))}
          testValueDisplay={fromC}
          testValueDisplayReverse={toC}
          locale={tempConv}
          multiplier='1'
          body={body || AMBIENT_TEMPERATURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'temperatureFLessThan':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor={`${AMBIENT_TEMPERATURE} (${tempConv === 'f' ? 'F' : 'C'})`}
          formula={formula || (tempConv === 'f' ? 'temperatureF' : 'temperatureC')}
          valueKey={valueKey || AMBIENT_TEMPERATURE_VALUEKEY}
          testValue={testValueOr((tempConv === 'f' ? 35 : 2))}
          multiplier={multiplier || 1}
          body={body || AMBIENT_TEMPERATURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'temperatureFGreaterThan':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'greaterThan'}
          sensor={`${AMBIENT_TEMPERATURE} (${tempConv === 'f' ? 'F' : 'C'})`}
          formula={formula || (tempConv === 'f' ? 'temperatureF' : 'temperatureC')}
          valueKey={valueKey || AMBIENT_TEMPERATURE_VALUEKEY}
          testValue={testValueOr((tempConv === 'f' ? 80 : 25))}
          multiplier={multiplier || 1}
          body={body || AMBIENT_TEMPERATURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'soilMoistureGreaterThan':
      formula = formula || 'soilMoistureSensorAverage'
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'greaterThan'}
          sensor={SOIL_MOISTURE}
          formula={formula}
          valueKey={soilMoistureValueKeys}
          testValue={testValueOr(40)}
          multiplier={multiplier || 100}
          body={body || formatNotificationMessage(AVERAGE_SOIL_MOISTURE_HIGH_MESSAGE, soilMoistureValueKeys, multiplier || 100)}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'soilMoistureLessThan':
      formula = formula || 'soilMoistureSensorAverage'
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor={SOIL_MOISTURE}
          formula={formula}
          valueKey={soilMoistureValueKeys}
          testValue={testValueOr(20)}
          multiplier={multiplier || 100}
          body={body || formatNotificationMessage(AVERAGE_SOIL_MOISTURE_LOW_MESSAGE, soilMoistureValueKeys, multiplier || 100)}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'soilTemperatureGreaterThan':
      formula = formula || 'soilTemperatureSensorAverage'
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'greaterThan'}
          sensor={`${SOIL_TEMPERATURE} (${tempConv === 'f' ? 'F' : 'C'})`}
          formula={formula}
          valueKey={soilTemperatureValueKeys}
          testValue={testValueOr((tempConv === 'f' ? 80 : 25))}
          multiplier={multiplier || 100}
          body={body || formatNotificationMessage(AVERAGE_SOIL_TEMPERATURE_HIGH_MESSAGE, soilTemperatureValueKeys, multiplier || 100)}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'soilTemperatureLessThan':
      formula = formula || 'soilTemperatureSensorAverage'
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor={`${SOIL_TEMPERATURE} (${tempConv === 'f' ? 'F' : 'C'})`}
          formula={formula}
          valueKey={soilTemperatureValueKeys}
          testValue={testValueOr((tempConv === 'f' ? 80 : 25))}
          multiplier={multiplier || 100}
          body={body || formatNotificationMessage(AVERAGE_SOIL_TEMPERATURE_LOW_MESSAGE, soilTemperatureValueKeys, multiplier || 100)}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'soilSalinityGreaterThan':
      formula = formula || 'soilSalinitySensorAverage'
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'greaterThan'}
          sensor={SOIL_SALINITY}
          formula={formula}
          valueKey={soilSalinityValueKeys}
          testValue={testValueOr(40)}
          multiplier={multiplier || 100}
          body={body || formatNotificationMessage(AVERAGE_SOIL_SALINITY_HIGH_MESSAGE, soilSalinityValueKeys, multiplier || 100)}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'soilSalinityLessThan':
      formula = formula || 'soilSalinitySensorAverage'
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor={SOIL_SALINITY}
          formula={formula}
          valueKey={soilSalinityValueKeys}
          testValue={testValueOr(20)}
          multiplier={multiplier || 100}
          body={body || formatNotificationMessage(AVERAGE_SOIL_SALINITY_LOW_MESSAGE, soilSalinityValueKeys, multiplier || 100)}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'batteryExternal':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor='Battery Voltage'
          formula={formula || 'percentTo20V'}
          valueKey={valueKey || BATTERY_VOLTAGE_VALUEKEY}
          testValue={testValueOr(8)}
          multiplier={multiplier || 1}
          body={body || BATTERY_VOLTAGE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'batteryExternalLessThan':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor='Battery Voltage'
          formula={formula || 'percentTo20V'}
          valueKey={valueKey || BATTERY_VOLTAGE_VALUEKEY}
          testValue={testValueOr(11.8)}
          multiplier={multiplier || 1}
          body={body || BATTERY_VOLTAGE_LOW_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'batteryExternalDisconnected':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor='Battery Voltage'
          formula={formula || 'percentTo20V'}
          valueKey={valueKey || BATTERY_VOLTAGE_VALUEKEY}
          testValue={testValueOr(2)}
          multiplier={multiplier || 1}
          body={body || BATTERY_VOLTAGE_DISCONNECTED_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'batteryInternal':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor='Altrac Battery %'
          formula={formula || 'default'}
          valueKey={valueKey || INTERNAL_BATTERY_VOLTAGE_VALUEKEY}
          testValue={testValueOr(20)}
          multiplier={multiplier || 0.01}
          body={body || INTERNAL_BATTERY_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'fuelLevel':
    case 'fuelLevelLessThan':
      ruleTemplate = (
        <Simple
          template={template}
          test={test || 'lessThan'}
          sensor='Fuel Level'
          formula={formula || 'fuelLevel'}
          valueKey={valueKey || FUEL_LEVEL_VALUEKEY}
          testValue={testValueOr(40)}
          multiplier={multiplier || 0.01}
          body={body || FUEL_LEVEL_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'engineRunning':
      ruleTemplate = (
        <EngineRunning
          template={template}
          test={test || 'greaterThan'}
          sensor='Engine Running'
          formula={formula || 'default'}
          valueKey={valueKey || ENGINE_RUNNING_VALUEKEY}
          testValue={testValueOr(0)}
          multiplier={multiplier || 1}
          body={body || WIND_MACHINE_RUNNING_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'engineRunningStart':
      ruleTemplate = (
        <EngineRunning
          template={template}
          test={test || 'greaterThan'}
          sensor='Engine Running'
          formula={formula || 'default'}
          valueKey={valueKey || ENGINE_RUNNING_VALUEKEY}
          testValue={testValueOr(0)}
          multiplier={multiplier || 1}
          body={body || WIND_MACHINE_STARTUP_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'engineRunningStop':
      ruleTemplate = (
        <EngineRunning
          template={template}
          test={test || 'lessThan'}
          sensor='Engine Running'
          formula={formula || 'default'}
          valueKey={valueKey || ENGINE_RUNNING_VALUEKEY}
          testValue={testValueOr(1)}
          multiplier={multiplier || 1}
          body={body || WIND_MACHINE_STOP_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    /*
     Add new rule to check if the wind machine failed to start. If the machine is
     trying to start but fails, the valueKey 140 will be 8.
    */
    case 'engineFailedToStart':
      ruleTemplate = (
        <EngineRunning
          template={template}
          test={test || 'equalTo'}
          sensor='Engine Running'
          formula={formula || 'default'}
          valueKey={valueKey || ENGINE_FAILED_TO_START_VALUEKEY}
          testValue={testValueOr(8)}
          multiplier={multiplier || 1}
          body={body || WIND_MACHINE_FAILED_TO_START_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'bindicator':
    case 'bindicatorGreaterThan':
      ruleTemplate = (
        <Bindicator
          template={template}
          test={test || 'greaterThan'}
          sensor='Bin Fullness'
          formula={formula || 'default'}
          valueKey={valueKey || ENGINE_RUNNING_VALUEKEY}
          testValue={testValueOr(1)}
          multiplier={multiplier || 1}
          body={body ||
            (powered
              ? 'Bin {{deviceNumber}} Status:\nBin 1: {{131,numberToBinaryFE1,1}}\nBin 2: {{131,numberToBinaryFE2,1}}\nBin 3: {{131,numberToBinaryFE3,1}}'
              : 'Bin {{deviceNumber}} Status:\nBin 1: {{131,numberToBinaryFE0,1}}\nBin 2: {{131,numberToBinaryFE1,1}}\nBin 3: {{131,numberToBinaryFE2,1}}\nBin 4: {{131,numberToBinaryFE3,1}}'
            )}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
          device={device}
        />
      )
      break
    case 'pressureLessThan':
      ruleTemplate = (
        <Simple
          template={template}
          test='lessThan'
          sensor='Pressure'
          formula='fourToTwenty'
          valueKey={133}
          testValue={20}
          multiplier='1'
          body={body || PRESSURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'pressureGreaterThan':
      ruleTemplate = (
        <Simple
          template={template}
          test='greaterThan'
          sensor='Pressure'
          formula='fourToTwenty'
          valueKey={133}
          testValue={60}
          multiplier='1'
          body={body || PRESSURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    case 'pressureHigh':
      ruleTemplate = (
        <PumpPressure
          body={body}
          debounceTime={debounceTime}
          device={device}
          formula={formula || 'default'}
          multiplier={multiplier || 1}
          notificationInterval={notificationInterval}
          sendOnce={!!sendOnce}
          sensor='Pressure'
          subject={subject}
          template={template}
          test={test || 'greaterThan'}
          testValue={testValueOr(0)}
          type={type}
          valueKey={valueKey}
        />
      )
      break
    case 'pressureLow':
      ruleTemplate = (
        <PumpPressure
          body={body}
          debounceTime={debounceTime}
          device={device}
          formula={formula || 'default'}
          multiplier={multiplier || 1}
          notificationInterval={notificationInterval}
          sendOnce={!!sendOnce}
          sensor='Pressure'
          sensors={{
            pressureBefore,
            pressureAfter
          }}
          subject={subject}
          template={template}
          test={test || 'lessThan'}
          testValue={testValueOr(0)}
          type={type}
          valueKey={valueKey}
        />
      )
      break
    case 'pumpRunning': {
      const test = (rule && rule.rule && rule.rule.test) ||
        (isPumpV1() ? 'pumpControllerRunning' : 'pumpRunning')

      ruleTemplate = (
        <PumpState
          device={device}
          formula='pumpRunning'
          notificationInterval={notificationInterval}
          report={report}
          sendOnce={!!sendOnce}
          subject={subject || deviceNumber}
          template='pumpRunning'
          test={test}
          valueKey={{
            run,
            signal
          }}
        />
      )
    }
      break
    case 'pumpStopped': {
      const test = (rule && rule.rule && rule.rule.test) ||
        (isPumpV1() ? 'pumpControllerEngineStopped' : 'pumpStopped')

      ruleTemplate = (
        <PumpState
          device={device}
          formula={formula || 'pumpStopped'}
          notificationInterval={notificationInterval}
          report={report}
          sendOnce={!!sendOnce}
          subject={subject || deviceNumber}
          template='pumpStopped'
          test={test}
          valueKey={{
            run,
            signal
          }}
        />
      )
    }
      break
    case 'powerDisconnected':
      ruleTemplate = (
        <EngineRunning
          body={POWER_DISCONNECTED_MESSAGE}
          debounceTime={debounceTime}
          formula='toBoolean'
          notificationInterval={notificationInterval}
          sensor='disconnected'
          sendOnce={!!sendOnce}
          subject={subject || deviceNumber}
          template={template || 'powerDisconnected'}
          test="equalTo'"
          testValue={false}
          type={type}
          valueKey={valueKey || 'PG'}
        />
      )
      break
    case 'general':
      ruleTemplate = (
        <General
          test={test || 'greaterThan'}
          formula={formula || 'temperatureF'}
          valueKey={String(valueKey || AMBIENT_TEMPERATURE_VALUEKEY)}
          testValue={testValueOr(32)}
          multiplier={multiplier || 1}
          body={body || AMBIENT_TEMPERATURE_MESSAGE}
          subject={subject}
          debounceTime={debounceTime}
          sendOnce={!!sendOnce}
          notificationInterval={notificationInterval}
          type={type}
        />
      )
      break
    default:
  }

  const alertFavorites = []

  if (deviceType === 'pump') {
    alertFavorites.push(
      {
        value: 'pumpRunning',
        name: formatMessage({
          id: 'rules.pump.running',
          defaultMessage: 'Pump running.',
        }),
        icon: <Pump running />,
      },
      {
        value: 'pumpStopped',
        name: formatMessage({
          id: 'rules.pump.stopped',
          defaultMessage: 'Pump stopped.',
        }),
        icon: <Pump stopped />,
      }
    )

    if (!!pressureAfter || !!pressureBefore) {
      alertFavorites.push(
        {
          value: 'pressureHigh',
          name: formatMessage({
            id: 'rules.pressure.high.alert',
            defaultMessage: 'Pressure is above X value.',
            description: 'pump pressure high alert label',
          }),
          icon: (<Pressure high />),
        },
        {
          value: 'pressureLow',
          name: formatMessage({
            id: 'rules.pressure.low.alert',
            defaultMessage: 'Pressure is below X value.',
            description: 'pump pressure low alert label',
          }),
          icon: (<Pressure low />),
        }
      )
    }

    if (pressureLegacy) {
      alertFavorites.push(
        {
          value: 'pressureGreaterThan',
          name: formatMessage({
            id: 'rules.pressure.high.alert',
            defaultMessage: 'Pressure is above X value.',
            description: 'pump pressure high alert label',
          }),
          icon: (<Pressure high />),
        },
        {
          value: 'pressureLessThan',
          name: formatMessage({
            id: 'rules.pressure.low.alert',
            defaultMessage: 'Pressure is below X value.',
            description: 'pump pressure low alert label',
          }),
          icon: (<Pressure low />),
        }
      )
    }
  }

  if (
    deviceType === 'windMachine' ||
    deviceType === 'coldAirDrain' ||
    deviceType === 'pumpFrostWater'
  ) {
    alertFavorites.push(
      {
        value: 'temperatureLessThan',
        name: formatMessage({
          id: 'rules.temperature.ambient.low',
          defaultMessage: 'Alert me when ambient temperature is low.',
          description: 'Ambient temp low heading',
        }),
        icon: (<Thermometer low />),
      },
      {
        value: 'temperatureGreaterThan',
        name: formatMessage({
          id: 'rules.temperature.ambient.high',
          defaultMessage: 'Alert me when ambient temperature is high.',
          description: 'Ambient temp high heading',
        }),
        icon: (<Thermometer high />),
      },
      {
        value: 'batteryExternalLessThan',
        name: formatMessage({
          id: 'rules.battery.external.low',
          defaultMessage: 'Alert me when battery voltage is low.',
          description: 'Battery voltage low heading',
        }),
        icon: (<Battery low />),
      },
      {
        value: 'batteryExternalDisconnected',
        name: formatMessage({
          id: 'rules.battery.external.disconnected',
          defaultMessage: 'Alert me when the battery is disconnected.',
          description: 'Battery disconnected heading',
        }),
        icon: (<Battery disconnected />),
      },
      {
        value: 'fuelLevelLessThan',
        name: formatMessage({
          id: 'rules.fuel.low',
          defaultMessage: 'Alert me when fuel level is low.',
          description: 'Fuel level low heading',
        }),
        icon: (<Fuel low />),
      },
      {
        value: 'engineRunningStart',
        name: formatMessage({
          id: 'rules.machineState.running',
          defaultMessage: 'Alert me when the wind machine is starting',
          description: 'Wind machine running heading',
        }),
        icon: (<SpeedometerOn />),
      },
      {
        value: 'engineRunningStop',
        name: formatMessage({
          id: 'rules.machineState.stopped',
          defaultMessage: 'Alert me when the wind machine is stopping',
          description: 'Wind machine stopped heading',
        }),
        icon: (<SpeedometerOff />),
      },
      {
        value: 'engineFailedToStart',
        name: formatMessage({
          id: 'rules.machineState.failedToStart',
          defaultMessage: 'Alert me when the wind machine fails to start',
          description: 'Wind machine failed to start heading',
        }),
        icon: (<SpeedometerOff />),
      }
    )

    if (level >= 20) {
      alertFavorites.push(
        {
          value: 'batteryInternal',
          name: formatMessage({
            id: 'rules.battery.internal.lessThan20',
            defaultMessage: 'Alert me when Altrac battery is less than 20%',
            description: 'Internal battery heading',
          }),
        }
      )
    }
  } else if (deviceType === 'moistureSensor' || deviceType === 'moisture') {
    alertFavorites.push(
      {
        value: 'soilMoistureLessThan',
        name: formatMessage({
          id: 'rules.soil.moisture.low',
          defaultMessage: 'Alert me when combined soil moisture is low.',
          description: 'combined soil moisture is low heading',
        }),
        icon: (<Moisture low />),
      },
      {
        value: 'soilMoistureGreaterThan',
        name: formatMessage({
          id: 'rules.soil.moisture.high',
          defaultMessage: 'Alert me when combined soil moisture is high.',
          description: 'combined soil moisture high heading',
        }),
        icon: (<Moisture high />),
      },
      {
        value: 'soilTemperatureLessThan',
        name: formatMessage({
          id: 'rules.soil.temperature.low',
          defaultMessage: 'Alert me when combined soil temperature is low.',
          description: 'combined soil temperature is low heading',
        }),
        icon: (<Thermometer low />),
      },
      {
        value: 'soilTemperatureGreaterThan',
        name: formatMessage({
          id: 'rules.soil.temperature.high',
          defaultMessage: 'Alert me when combined soil temperature is high.',
          description: 'combined soil temperature high heading',
        }),
        icon: (<Thermometer high />),
      }
    )

    if (soilSalinityValueKeys.length) {
      alertFavorites.push(
        {
          value: 'soilSalinityLessThan',
          name: formatMessage({
            id: 'rules.soil.salinity.low',
            defaultMessage: 'Alert me when soil salinity is low.',
            description: 'combined soil salinity is low heading',
          }),
          icon: <Low />,
        },
        {
          value: 'soilSalinityGreaterThan',
          name: formatMessage({
            id: 'rules.soil.salinity.high',
            defaultMessage: 'Alert me when combined soil salinity is high.',
            description: 'combined soil salinity high heading',
          }),
          icon: <High />,
        }
      )
    }
  } else if (deviceType === 'temperature') {
    alertFavorites.push(
      {
        value: 'batteryExternalLessThan',
        name: formatMessage({
          id: 'rules.battery.external.low',
          defaultMessage: 'Alert me when battery voltage is low.',
          description: 'Battery voltage low heading',
        }),
        icon: (<Battery low />),
      },
      {
        value: 'batteryExternalDisconnected',
        name: formatMessage({
          id: 'rules.battery.external.disconnected',
          defaultMessage: 'Alert me when the battery is disconnected.',
          description: 'Battery disconnected heading',
        }),
        icon: (<Battery disconnected />),
      },
      {
        value: 'temperatureLessThan',
        name: formatMessage({
          id: 'rules.temperature.ambient.low',
          defaultMessage: 'Alert me when ambient temperature is low.',
          description: 'Ambient temp low heading',
        }),
        icon: (<Thermometer low />),
      },
      {
        value: 'temperatureGreaterThan',
        name: formatMessage({
          id: 'rules.temperature.ambient.high',
          defaultMessage: 'Alert me when ambient temperature is high.',
          description: 'Ambient temp high heading',
        }),
        icon: (<Thermometer high />),
      }
    )

    if (level >= 20) {
      alertFavorites.push(
        {
          value: 'batteryInternal',
          name: formatMessage({
            id: 'rules.battery.internal.lessThan20',
            defaultMessage: 'Alert me when Altrac battery is less than 20%',
            description: 'Internal battery heading',
          }),
        }
      )
    }
  } else if (deviceType === 'binDicator') {
    alertFavorites.push(
      {
        value: 'bindicatorGreaterThan',
        name: 'Alert me when bins are full.',
      }
    )

    if (level >= 20) {
      alertFavorites.push(
        {
          value: 'batteryInternal',
          name: formatMessage({
            id: 'rules.battery.internal.lessThan20',
            defaultMessage: 'Alert me when Altrac battery is less than 20%',
            description: 'Internal battery heading',
          }),
        }
      )
    }
  }

  let ruleCustomize
  if (template) {
    ruleCustomize = (
      <div className={`${styles.ruleInput} ${styles.ruleList}`}>
        {template === 'general' && (
          <FormattedMessage
            id='rules.customRule'
            defaultMessage='Custom Rule'
            description='Custom Rule'
            tagName='h3'
          />
        )}
        {template !== 'general' && (
          <FormattedMessage
            id='rules.customize'
            defaultMessage='Customize'
            description='Customize heading'
            tagName='h3'
          />
        )}
        {ruleTemplate}
        <br />
        <FormattedMessage
          id='rules.contact.name'
          defaultMessage='Contact Information'
          description='Contact Information heading'
          tagName='h3'
        />
        <InputContactInfo
          method={method || 'sms'}
          addresses={addresses || ['']}
        />
      </div>
    )
  }

  return (
    <div data-name='RuleDetail'>
      <section>
        <div>
          <ButtonList
            value={template}
            field='template'
            childChange={childChange}
            options={alertFavorites}
            disabled={disabledSelect}
          />
        </div>
      </section>
      <section>
        {ruleCustomize}
      </section>
    </div>
  )
}

RuleDetail.propTypes = {
  rule: PropTypes.object,
  saveButtonCallback: PropTypes.func.isRequired,
  device: PropTypes.object,
  address: PropTypes.string,
  level: PropTypes.number.isRequired,
  tempConv: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
}

export default withAppStore(injectIntl(RuleDetail))
